import { supabase } from '@/lib/supabase';
import type { TemplateSender } from './printTemplates';

export interface SavedPrintSender {
  sender: TemplateSender;
  version: number;
}
const fields = 'organization_id,sender_name,sender_address,sender_phone,version';
const conflictMessage =
  'The saved address changed. Your edits are kept. Reload the saved address before saving again.';

export function normalizePrintSender(sender: TemplateSender): TemplateSender {
  const value = {
    name: sender.name.trim(),
    address: sender.address.trim(),
    phone: sender.phone.trim(),
  };
  if (
    !value.name ||
    !value.address ||
    value.name.length > 80 ||
    value.address.length > 300 ||
    value.phone.length > 30
  )
    throw new Error(
      'Enter a name (up to 80 characters), address (up to 300) and optional phone (up to 30).',
    );
  return value;
}

function savedRow(data: any, organizationId: string): SavedPrintSender {
  if (
    data?.organization_id !== organizationId ||
    !Number.isSafeInteger(data.version) ||
    data.version < 1 ||
    typeof data.sender_name !== 'string' ||
    typeof data.sender_address !== 'string' ||
    typeof data.sender_phone !== 'string'
  )
    throw new Error('The saved address could not be verified. Please reload and try again.');
  return {
    sender: normalizePrintSender({
      name: data.sender_name,
      address: data.sender_address,
      phone: data.sender_phone,
    }),
    version: data.version,
  };
}

function databaseError(error: { code?: string }): Error {
  if (error.code === '23505') return new Error(conflictMessage);
  if (['42P01', 'PGRST205'].includes(error.code ?? ''))
    return new Error(
      'Saved addresses need the print settings database update. Ask your administrator to finish the setup.',
    );
  if (error.code === '42501')
    return new Error('Only Super Admin can save the shared sender address.');
  if (error.code === '23514' || error.code === '23502')
    return new Error('Check the sender name, full address and phone length before saving.');
  return new Error(
    'The saved address service could not be reached. Your entries are kept. Please retry.',
  );
}

export async function loadPrintSender(
  organizationId: string,
  signal: AbortSignal,
): Promise<SavedPrintSender | null> {
  const { data, error } = await supabase
    .from('print_sender_settings')
    .select(fields)
    .eq('organization_id', organizationId)
    .abortSignal(signal)
    .maybeSingle();
  if (error) throw databaseError(error);
  if (signal.aborted) throw new Error('Address request cancelled.');
  return data ? savedRow(data, organizationId) : null;
}

export async function savePrintSender(
  organizationId: string,
  sender: TemplateSender,
  version: number | null,
  signal: AbortSignal,
): Promise<SavedPrintSender> {
  const value = normalizePrintSender(sender);
  const payload = {
    sender_name: value.name,
    sender_address: value.address,
    sender_phone: value.phone,
  };
  const mutation =
    version === null
      ? supabase
          .from('print_sender_settings')
          .insert({ organization_id: organizationId, ...payload })
      : supabase
          .from('print_sender_settings')
          .update(payload)
          .eq('organization_id', organizationId)
          .eq('version', version);
  const { data, error } = await mutation.select(fields).abortSignal(signal).maybeSingle();
  if (error) throw databaseError(error);
  if (signal.aborted) throw new Error('Address request cancelled.');
  if (!data) throw new Error(conflictMessage);
  return savedRow(data, organizationId);
}
